import { useState, useEffect } from "react";
import Logo from "../../assets/COTLogoT.png";
import { useNavigate } from "react-router-dom";
import { Menu, ShoppingCart, X, ChevronRight, ArrowLeft } from "lucide-react";

const navItems = [
  { name: "Home", link: "/" },
  {
    name: "Products",
    children: [
      { name: "Anti-tarnish Jewellery", 
        children: [
          { name: "Necklaces", link: "/anti-tarnish-jewellery/necklaces" },
          { name: "Earrings", link: "/anti-tarnish-jewellery/earrings" },
          { name: "Bracelets", link: "/anti-tarnish-jewellery/bracelets" },
          { name: "Rings", link: "/anti-tarnish-jewellery/rings" },
        ],
        link: "/anti-tarnish-jewellery" 
      },
      { name: "Korean jewellery", 
        children: [
          { name: "Necklaces", link: "/korean-jewellery/necklaces" },
          { name: "Earrings", link: "/korean-jewellery/earrings" },
        ],
        link: "/korean-jewellery" 
      },
      { name: "Resin jewellery", 
        children: [
          { name: "Rings", link: "/resin-jewellery/rings" },
          { name: "Necklaces", link: "/resin-jewellery/necklaces" },
          { name: "Earrings", link: "/resin-jewellery/earrings" },
        ],
        link: "/resin-jewellery" 
      },
      { name: "Enamel jewellery", 
        link: "/enamel-jewellery" 
      },
      { name: "Tulip bracelets", 
        link: "/tulip-bracelets" 
      },
      { name: "Gift box", 
        link: "/gift-box" 
      },  
    ],
  },
  { name: "Contact", link: "/contact" },
  { name: "FAQ", link: "/faq" },
];

export default function Navbar({ cart = [], increaseQty, decreaseQty, checkoutWhatsApp }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [stack, setStack] = useState([navItems]);

  const navigate = useNavigate();

  const currentLevel = stack[stack.length - 1];

  // ✅ Scroll lock (FIXED SAFELY)
  useEffect(() => {
    const original = document.body.style.overflow;
    if (menuOpen || cartOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = original || "auto";
    }
    return () => (document.body.style.overflow = original || "auto");
  }, [menuOpen, cartOpen]);

  const openNext = (item) => {
    if (item.children) {
      setStack([...stack, item.children]);
    } else {
      navigate(item.link);
      setMenuOpen(false);
      setStack([navItems]);
    }
  };

  const goBack = () => {
    if (stack.length > 1) {
      setStack(stack.slice(0, -1));
    }
  };

  // ✅ RESTORED ORIGINAL LOGIC
  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const savings = cart.reduce(
    (sum, item) => sum + (item.originalPrice - item.price) * item.quantity,
    0
  );

  return (
    <>
      {/* 🔝 NAVBAR */}
      <nav style={styles.nav}>
        <div onClick={() => setMenuOpen(true)} style={styles.icon}>
          <Menu size={24} strokeWidth={1.8} />
        </div>

        <div style={styles.logoWrapper} onClick={() => navigate("/")}>
          <img src={Logo} alt="logo" style={styles.logo} />
        </div>

        <div onClick={() => setCartOpen(true)} style={styles.icon}>
          <ShoppingCart size={24} strokeWidth={1.8} />
          {cart.length > 0 && <span style={styles.badge}>{cart.length}</span>}
        </div>
      </nav>

      {/* 📱 FULL SCREEN MENU */}
      <div style={{ ...styles.menu, transform: menuOpen ? "translateX(0)" : "translateX(-100%)" }}>
        <div style={styles.menuHeader}>
          {stack.length > 1 && (
            <div onClick={goBack}
              style={styles.iconBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(125,18,1,0.15)";
                e.currentTarget.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(125,18,1,0.08)";
                e.currentTarget.style.transform = "scale(1)";
              }}><ArrowLeft size={20} /></div>
          )}
          <div
            onClick={() => setMenuOpen(false)}
            style={styles.iconBtn}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(125,18,1,0.15)";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(125,18,1,0.08)";
              e.currentTarget.style.transform = "scale(1)";
            }}><X size={22} /></div>
        </div>

        {currentLevel.map((item) => (
          <div key={item.name} style={styles.menuItem} onClick={() => openNext(item)}>
            {item.name}
            {item.children && <span><ChevronRight size={18} /></span>}
          </div>
        ))}
      </div>

      {/* 🛒 CART */}
      <div style={{ ...styles.cart, transform: cartOpen ? "translateX(0)" : "translateX(100%)" }}>
        <div style={styles.cartHeader}>
          <div style={styles.cartTitle}>Your Cart</div>

          <div
            style={styles.closeIcon}
            onClick={() => setCartOpen(false)}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(125,18,1,0.15)";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(125,18,1,0.08)";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <X size={20} />
          </div>
        </div>

        <div style={styles.cartContent}>
          {cart.length === 0 ? (
            <p>Cart is empty</p>
          ) : (
            cart.map((item) => (
              <div key={`${item.id}-${item.selectedColor}`} style={styles.cartItem}>
                <img src={item.image} style={styles.cartImg} alt="item" />

                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0 }}>{item.name}</p>

                  {item.selectedColor && (
                    <p style={styles.subText}>{item.selectedColor}</p>
                  )}

                  <div style={styles.priceRow}>
                    <span>₹{item.price}</span>
                    <span style={styles.strike}>₹{item.originalPrice}</span>
                  </div>

                  <div style={styles.qtyRow}>
                    <button style={styles.qtyBtn} onClick={() => decreaseQty(item)}>-</button>
                    <span>{item.quantity}</span>
                    <button style={styles.qtyBtn} onClick={() => increaseQty(item)}>+</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* ✅ RESTORED TOTAL SECTION */}
        {cart.length > 0 && (
          <div style={styles.checkout}>
            <div style={styles.totalRow}>
              <span style={styles.strike}>₹{total + savings}</span>
              <span style={styles.final}>₹{total}</span>
            </div>

            <p style={styles.savings}>🎉 You saved ₹{savings}</p>

            <button style={styles.checkoutBtn} onClick={checkoutWhatsApp}>
              Checkout
            </button>
          </div>
        )}
      </div>

      {/* OVERLAY */}
      <div
        style={{
          ...styles.overlay,
          opacity: menuOpen || cartOpen ? 1 : 0,
          pointerEvents: menuOpen || cartOpen ? "auto" : "none",
        }}
        onClick={() => {
          setMenuOpen(false);
          setCartOpen(false);
        }}
      />
    </>
  );
}

const styles = {
  nav: {
    position: "fixed",
    top: 36,
    left: 0,     
    width: "100%",
    height: "60px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0 16px",
    background: "#fff",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    zIndex: 1000,
  },
  iconBtn: {
    width: "36px",
    height: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",   // ✅ makes it circular
    cursor: "pointer",
    background: "rgba(125,18,1,0.08)",
    color: "#7D1201",
    transition: "all 0.2s ease"
  },

  logo: {
    height: "74px",
    objectFit: "contain",
    cursor: "pointer",
    transform: "scale(1.2)",
    transformOrigin: "center",
  },
  logoWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1   // keeps it perfectly centered between icons
},
  icon: {
    width: "42px",
    height: "42px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    cursor: "pointer",
    position: "relative",
    background: "rgba(125,18,1,0.08)",
    transition: "all 0.2s ease",
    color: "#7D1201"
  },

  badge: {
    position: "absolute",
    top: "-4px",
    right: "-4px",
    background: "#7D1201",
    color: "#fff",
    fontSize: "10px",
    borderRadius: "50%",
    padding: "3px 6px",
    fontWeight: "600"
  },

  menu: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100vh",
    background: "#fff",
    transition: "0.3s ease",
    zIndex: 1200,
    padding: "20px",
  },

  menuHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "20px",
  },

  back: { cursor: "pointer" },

  menuItem: {
    padding: "16px 0",
    fontSize: "18px",
    borderBottom: "1px solid #eee",
    display: "flex",
    justifyContent: "space-between",
    cursor: "pointer",
  },

  cart: {
    position: "fixed",
    right: 0,
    top: 0,
    width: "320px",
    height: "100vh",
    background: "#fff",
    transition: "0.3s ease",
    zIndex: 1300,
    display: "flex",
    flexDirection: "column",
    boxShadow: "-10px 0 30px rgba(0,0,0,0.1)",
  },

  cartContent: {
    flex: 1,
    overflowY: "auto",
    padding: "16px",
    paddingTop: "10px",
    paddingBottom: "140px" // reserve space for sticky checkout
  },

  cartItem: {
    display: "flex",
    gap: "12px",
    marginBottom: "14px",
  },

  cartImg: {
    width: "64px",
    height: "64px",
    objectFit: "cover",
    borderRadius: "8px",
  },

  subText: { fontSize: "12px", color: "#777", margin: 0 },

  priceRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    marginTop: "4px",
  },

  strike: {
    textDecoration: "line-through",
    color: "#999",
    fontSize: "13px",
  },

  qtyRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: "8px",
  },

  qtyBtn: {
    width: "28px",
    height: "28px",
    borderRadius: "6px",
    border: "none",
    background: "#f3f3f3",
    cursor: "pointer",
  },

  checkout: {
    padding: "16px",
    borderTop: "1px solid #eee",
    position: "sticky",
    bottom: 0,
    background: "#fff",
    zIndex: 2,
  },

  totalRow: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },

  final: {
    fontSize: "18px",
    fontWeight: "600",
  },

  savings: {
    color: "#2e7d32",
    fontSize: "14px",
    marginTop: "4px",
  },

  checkoutBtn: {
    width: "100%",
    padding: "12px",
    borderRadius: "10px",
    border: "none",
    background: "linear-gradient(135deg, #7D1201, #A61B0F)",
    color: "#fff",
    fontWeight: "600",
    cursor: "pointer",
    marginTop: "10px",
  },

  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.3)",
    zIndex: 1100,
  },
  cartHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 16px",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    background: "#fff",
    position: "sticky",
    top: 0,
    zIndex: 2
  },

  cartTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#7D1201",   // cherry red
    letterSpacing: "0.3px"
  },

  closeIcon: {
    width: "36px",
    height: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    cursor: "pointer",
    background: "rgba(125,18,1,0.08)",
    color: "#7D1201",
    transition: "all 0.2s ease"
  },
};
